import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scissors, Clock, MapPin, Phone, Instagram, Facebook, ChevronRight, Star, ExternalLink, Share2, X, Download, MessageCircle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { servicesAPI, barbersAPI, promotionsAPI, vipPerksAPI, mediaAPI } from '../lib/api';
import { QRCodeCanvas } from 'qrcode.react';

// Custom images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/2ufwantd_IMG_8386.jpeg";

// El Patron Products
const EL_PATRON_IMAGE = "";

const DEFAULT_INSTAGRAM_IMAGES = [
    "https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/09yjlrnx_IMG_8426.jpeg",
    "https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/jfr6xlbn_IMG_7960.png",
    "https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/tb8iz08j_IMG_7958.jpeg",
    HERO_IMAGE
];

const EL_PATRON_PRODUCTS = [
    { name: "Sea Drops", price: 15.99 },
    { name: "Cera in Polvere", price: 11.99 },
    { name: "Cera Lucida", price: 9.99 },
    { name: "Cera Matt", price: 10.99 },
    { name: "Cera Pomade", price: 10.99 },
    { name: "Curl Control", price: 15.99 },
    { name: "Gel", price: 11.99 },
    { name: "Gel Wax", price: 13.99 },
    { name: "Cera Liquida Modellante", price: 8.00 },
];

const DEFAULT_BANNER = {
    title: "Stay tuned for our new promos",
    message: "",
    cta_text: "",
    cta_link: ""
};

const REVIEWS = [
    {
        name: "Davide Santoro",
        service: "Skin Fade",
        rating: 5,
        text: "Best fade I’ve had in Malta. Clean, sharp and on time."
    },
    {
        name: "Alessio Giuseppe",
        service: "Haircut & Beard",
        rating: 5,
        text: "Super professional team. They always listen and deliver."
    },
    {
        name: "Gabriel Azzopardi",
        service: "Beard Grooming",
        rating: 5,
        text: "Top service and great vibe inside the shop. Highly recommended."
    }
];

const DEFAULT_VIP_PERKS = {
    title: "VIP PERKS",
    description: "Priority slots and seasonal perks for loyal clients."
};

// Location coordinates for Google Maps
const LOCATIONS = {
    swieqi: {
        name: "Swieqi Branch",
        address: "Triq is-Swieqi 4, Swieqi, Malta",
        barbers: "Riccardo & Abde",
        mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3231.5!2d14.4789!3d35.9247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x130e452d3081f9f7%3A0x7a12345678!2sTriq%20is-Swieqi%2C%20Swieqi%2C%20Malta!5e0!3m2!1sen!2s!4v1234567890",
        directionsUrl: "https://www.google.com/maps/dir/?api=1&destination=Triq+is-Swieqi+4,+Swieqi,+Malta"
    }
};

const LandingPage = () => {
    const [services, setServices] = useState([]);
    const [barbers, setBarbers] = useState([]);
    const [promoBanner, setPromoBanner] = useState(DEFAULT_BANNER);
    const [vipPerks, setVipPerks] = useState(DEFAULT_VIP_PERKS);
    const [mediaSettings, setMediaSettings] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [copyStatus, setCopyStatus] = useState('');
    const qrRef = useRef(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                // Load services and barbers
                const [servicesRes, barbersRes, promoRes, vipRes, mediaRes] = await Promise.all([
                    servicesAPI.getAll(),
                    barbersAPI.getAll(),
                    promotionsAPI.getBanner(),
                    vipPerksAPI.get(),
                    mediaAPI.getSettings()
                ]);
                setServices(servicesRes.data);
                setBarbers(barbersRes.data);
                setPromoBanner(promoRes?.data || DEFAULT_BANNER);
                setVipPerks(vipRes?.data || DEFAULT_VIP_PERKS);
                setMediaSettings(mediaRes?.data || null);
            } catch (error) {
                console.error('Failed to load data:', error);
                setPromoBanner(DEFAULT_BANNER);
                setVipPerks(DEFAULT_VIP_PERKS);
            }
        };
        loadData();
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setShareUrl(window.location.origin);
        }
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return;

        const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
        document.title = 'RL Barbershop | Malta';

        const ensureMeta = (selector, attr, value) => {
            let element = document.querySelector(selector);
            if (!element) {
                element = document.createElement('meta');
                if (attr === 'name') {
                    element.setAttribute('name', selector.replace("meta[name='", '').replace("']", ''));
                }
                if (attr === 'property') {
                    element.setAttribute('property', selector.replace("meta[property='", '').replace("']", ''));
                }
                document.head.appendChild(element);
            }
            element.setAttribute('content', value);
        };

        ensureMeta("meta[name='description']", 'name', 'RL Barbershop Malta — book premium haircuts, skin fades, and grooming in Swieqi.');
        ensureMeta("meta[name='keywords']", 'name', 'RL Barbershop, Malta barber, Swieqi barbershop, skin fade, haircut booking');
        ensureMeta("meta[property='og:title']", 'property', 'RL Barbershop | Malta');
        ensureMeta("meta[property='og:description']", 'property', 'Book your next cut at RL Barbershop in Swieqi, Malta.');
        ensureMeta("meta[property='og:type']", 'property', 'website');
        ensureMeta("meta[property='og:url']", 'property', baseUrl);
        ensureMeta("meta[name='twitter:card']", 'name', 'summary_large_image');
        ensureMeta("meta[name='twitter:title']", 'name', 'RL Barbershop | Malta');
        ensureMeta("meta[name='twitter:description']", 'name', 'Premium haircuts, fades & grooming. Book online in Swieqi.');

        const structuredData = {
            '@context': 'https://schema.org',
            '@type': 'HairSalon',
            name: 'RL Barbershop',
            image: 'https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/2ufwantd_IMG_8386.jpeg',
            telephone: '+356 9933 6503',
            url: baseUrl,
            address: [
                {
                    '@type': 'PostalAddress',
                    streetAddress: 'Triq is-Swieqi 4, Swieqi, Malta',
                    addressLocality: 'Swieqi',
                    addressCountry: 'MT'
                }
            ],
            openingHours: 'Mo-Sa 09:00-19:00',
            sameAs: [
                'https://www.instagram.com/rlbarber.shop/',
                'https://www.facebook.com/RiccardoLaudaniBarbershop'
            ]
        };

        let jsonLd = document.getElementById('rlbarbershop-structured-data');
        if (!jsonLd) {
            jsonLd = document.createElement('script');
            jsonLd.id = 'rlbarbershop-structured-data';
            jsonLd.type = 'application/ld+json';
            document.head.appendChild(jsonLd);
        }
        jsonLd.textContent = JSON.stringify(structuredData);
    }, []);

    useEffect(() => {
        if (!shareOpen) {
            setCopyStatus('');
        }
    }, [shareOpen]);

    const resolveMediaUrl = (value) => {
        if (!value) return '';
        if (value.startsWith('http')) return value;
        return `${process.env.REACT_APP_BACKEND_URL}${value}`;
    };

    const heroImage = resolveMediaUrl(mediaSettings?.hero_image || HERO_IMAGE);
    const productImage = resolveMediaUrl(mediaSettings?.product_image || EL_PATRON_IMAGE);
    const instagramImages = (mediaSettings?.instagram_images?.length ? mediaSettings.instagram_images : DEFAULT_INSTAGRAM_IMAGES)
        .map((img) => resolveMediaUrl(img));

    const DEFAULT_SERVICE_IMAGES = {
        'Skin Fade': 'https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/c8phz6q9_IMG_5595.jpeg',
        'Normal haircut': 'https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/ghbn0ey4_IMG_8019.jpeg',
        'Beard': 'https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/oikqzbg8_IMG_4601.jpeg',
    };

    const featuredServices = useMemo(() => {
        const serviceImages = mediaSettings?.service_images || {};
        const featuredKeys = mediaSettings?.featured_services || [];

        // Use admin-selected featured services if available
        const sourceServices = featuredKeys.length > 0
            ? featuredKeys.map(key => services.find(s => s.key === key)).filter(Boolean)
            : services.slice(0, 3);

        const featured = sourceServices.slice(0, 3).map((svc) => {
            const adminImage = serviceImages[svc.key] ? resolveMediaUrl(serviceImages[svc.key]) : null;
            const defaultImage = Object.entries(DEFAULT_SERVICE_IMAGES).find(([keyword]) =>
                (svc.name || '').toLowerCase().includes(keyword.toLowerCase())
            )?.[1];
            return {
                key: svc.key,
                name: (svc.name || '').replace(/\s*\(Swieqi Branch\)/i, ''),
                price: `€${svc.price || 0}`,
                image: adminImage || defaultImage || 'https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/c8phz6q9_IMG_5595.jpeg',
            };
        });
        if (featured.length) return featured;
        // Fallback if no services loaded
        return [
            { name: 'Skin Fade', price: '€15', image: DEFAULT_SERVICE_IMAGES['Skin Fade'] },
            { name: 'Normal Haircut', price: '€15', image: DEFAULT_SERVICE_IMAGES['Normal haircut'] },
            { name: 'Beard Grooming', price: '€10', image: DEFAULT_SERVICE_IMAGES['Beard'] },
        ];
    }, [services, mediaSettings]);

    const bannerTitle = promoBanner?.title || DEFAULT_BANNER.title;
    const bannerMessage = promoBanner?.message || '';
    const bannerCtaText = promoBanner?.cta_text || '';
    const bannerCtaLink = promoBanner?.cta_link || '/book';
    const showBannerCta = bannerCtaText && bannerCtaLink;
    const isExternalBannerLink = bannerCtaLink?.startsWith('http');
    const resolvedShareUrl = shareUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    const whatsappShareUrl = resolvedShareUrl
        ? `https://wa.me/?text=${encodeURIComponent(resolvedShareUrl)}`
        : '';

    const handleCopyLink = async () => {
        if (!resolvedShareUrl) return;
        let copied = false;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(resolvedShareUrl);
                copied = true;
            }
        } catch (error) {
            copied = false;
        }

        if (!copied) {
            try {
                const tempInput = document.createElement('textarea');
                tempInput.value = resolvedShareUrl;
                tempInput.setAttribute('readonly', '');
                tempInput.style.position = 'absolute';
                tempInput.style.left = '-9999px';
                document.body.appendChild(tempInput);
                tempInput.select();
                tempInput.setSelectionRange(0, tempInput.value.length);
                copied = document.execCommand('copy');
                document.body.removeChild(tempInput);
            } catch (error) {
                copied = false;
            }
        }

        setCopyStatus(copied ? 'Copied!' : 'Copy failed');
    };

    const handleDownloadQR = () => {
        if (!qrRef.current) return;
        const canvas = qrRef.current.tagName === 'CANVAS'
            ? qrRef.current
            : qrRef.current.querySelector('canvas');
        if (!canvas) return;
        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = 'rlbarbershop-qr.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="min-h-screen bg-dark">
            <Navbar />

            {/* Promotional Banner */}
            <section className="pt-24 px-6 md:px-12" data-testid="promo-banner">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-accent-yellow text-black px-6 py-4 border border-black">
                        <div>
                            <span className="font-anton text-lg">{bannerTitle}</span>
                            {bannerMessage && (
                                <p className="font-urbanist text-sm">{bannerMessage}</p>
                            )}
                        </div>
                        {showBannerCta && (
                            isExternalBannerLink ? (
                                <a
                                    href={bannerCtaLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="promo-banner-cta"
                                >
                                    <Button className="bg-black text-white font-anton tracking-wider px-6 py-3 hover:bg-zinc-900">
                                        {bannerCtaText}
                                    </Button>
                                </a>
                            ) : (
                                <Link to={bannerCtaLink} data-testid="promo-banner-cta">
                                    <Button className="bg-black text-white font-anton tracking-wider px-6 py-3 hover:bg-zinc-900">
                                        {bannerCtaText}
                                    </Button>
                                </Link>
                            )
                        )}
                    </div>
                </div>
            </section>

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center" data-testid="hero-section">
                {/* Background Image - Your actual shop photo */}
                <div className="absolute inset-0 z-0">
                    <img
                        src={heroImage}
                        alt="RL Barbershop"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/70 to-dark/40" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="inline-block px-4 py-2 bg-accent-pink/20 text-accent-pink font-urbanist font-semibold text-sm mb-6">
                                PREMIUM BARBERSHOP IN MALTA
                            </span>
                            <h1 className="font-anton text-5xl sm:text-6xl lg:text-7xl leading-none mb-6">
                                STYLE THAT<br />
                                <span className="text-primary">SPEAKS</span><br />
                                VOLUMES
                            </h1>
                            <p className="font-urbanist text-xl text-zinc-400 mb-8 max-w-lg">
                                Experience premium grooming at RL Barbershop. Serving you with expertise and style since day one.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/book" data-testid="hero-book-btn">
                                    <Button className="bg-primary text-black font-anton text-lg tracking-wider px-8 py-6 hover:bg-white neon-glow shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none">
                                        <Scissors className="mr-2" />
                                        BOOK YOUR CUT
                                    </Button>
                                </Link>
                                <Link to="/services" data-testid="hero-services-btn">
                                    <Button variant="outline" className="font-urbanist font-bold text-lg tracking-wider px-8 py-6 border-2 border-white text-white hover:bg-white hover:text-black">
                                        VIEW SERVICES
                                        <ChevronRight className="ml-2" />
                                    </Button>
                                </Link>
                                <Button
                                    variant="outline"
                                    onClick={() => setShareOpen(true)}
                                    data-testid="share-qr-button"
                                    className="font-urbanist font-bold text-lg tracking-wider px-8 py-6 border-2 border-zinc-600 text-zinc-300 hover:bg-white hover:text-black"
                                >
                                    <Share2 className="mr-2" />
                                    SHARE
                                </Button>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="hidden lg:block"
                        >
                            <div className="relative">
                                <div className="absolute -top-4 -left-4 w-full h-full border-2 border-primary" />
                                <img
                                    src="https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/03303zlc_96f21524-c6ba-4411-86fb-3aa42208d697.jpeg"
                                    alt="RL Logo"
                                    className="w-full max-w-md mx-auto"
                                />
                            </div>
                        </motion.div>
                    </div>

                    {/* Quick Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
                    >
                        {[
                            { icon: Clock, label: 'Mon-Sat', value: '9AM - 7PM' },
                            { icon: MapPin, label: 'Location', value: 'Swieqi, Malta' },
                            { icon: Phone, label: 'Call Us', value: '+356 9933 6503' },
                            { icon: Star, label: 'Experience', value: 'Since 2020' },
                        ].map((item) => (
                            <div key={item.label} className="glass p-4">
                                <item.icon className="text-primary mb-2" size={24} />
                                <p className="text-zinc-500 text-sm font-urbanist">{item.label}</p>
                                <p className="text-white font-urbanist font-semibold">{item.value}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Featured Services */}
            <section className="py-24 px-6 md:px-12 bg-mural-light" data-testid="services-section">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
                        <div>
                            <span className="text-accent-pink font-urbanist font-semibold text-sm mb-2 block">WHAT WE DO</span>
                            <h2 className="font-anton text-4xl md:text-5xl">OUR SERVICES</h2>
                        </div>
                        <Link
                            to="/services"
                            className="mt-4 md:mt-0 text-zinc-400 hover:text-primary font-urbanist flex items-center gap-2"
                            data-testid="services-view-all-link"
                        >
                            View All Services <ChevronRight size={18} />
                        </Link>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {featuredServices.map((service, i) => (
                            <motion.div
                                key={service.key || service.id || `service-${i}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="service-card card-hover group cursor-pointer"
                                data-testid={`service-card-${i}`}
                            >
                                <div className="relative h-64 overflow-hidden bg-zinc-900">
                                    <img
                                        src={service.image}
                                        alt={service.name}
                                        loading="lazy"
                                        className="service-card-image w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        draggable="false"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/30 to-transparent pointer-events-none" />
                                    <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                                        <span className="text-primary font-anton text-2xl">{service.price}</span>
                                        <h3 className="font-anton text-xl text-white">{service.name}</h3>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-24 px-6 md:px-12 bg-mural" data-testid="team-section">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-accent-cyan font-urbanist font-semibold text-sm mb-2 block">THE CREW</span>
                        <h2 className="font-anton text-4xl md:text-5xl">MEET OUR BARBERS</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {barbers.map((barber, i) => (
                            <motion.div
                                key={barber.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="text-center group"
                                data-testid={`barber-card-${i}`}
                            >
                                <div className="relative w-48 h-48 mx-auto mb-6">
                                    <div className="absolute inset-0 border-2 border-primary transform rotate-3 group-hover:rotate-6 transition-transform" />
                                    <img
                                        src={barber.image_url || `https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400`}
                                        alt={barber.name}
                                        className="w-full h-full object-cover img-grayscale group-hover:filter-none transition-all duration-500"
                                    />
                                </div>
                                <h3 className="font-anton text-2xl text-white mb-1">{barber.name}</h3>
                                <p className="text-zinc-500 font-urbanist text-sm">
                                    {barber.specialties?.join(' • ')}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* El Patron Products Section */}
            <section className="py-24 px-6 md:px-12 bg-mural-light" data-testid="products-section">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-amber-500 font-urbanist font-semibold text-sm mb-2 block">OUR PRODUCTS</span>
                        <h2 className="font-anton text-4xl md:text-5xl">EL PATRON</h2>
                        <p className="text-amber-500/80 font-urbanist italic mt-2">Professional Line</p>
                    </div>

                    {productImage && (
                        <div className="flex justify-center mb-10" data-testid="product-hero-image">
                            <img
                                src={productImage}
                                alt="El Patron"
                                className="h-36 w-auto object-contain"
                            />
                        </div>
                    )}

                    {/* Products Grid - same style as services */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {EL_PATRON_PRODUCTS.map((product, i) => (
                            <motion.div
                                key={product.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                                className="bg-dark border border-zinc-800 p-6 hover:border-amber-500 transition-all group"
                                data-testid={`product-${product.id}`}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <span className="font-anton text-2xl text-amber-500 mb-3">
                                        €{product.price.toFixed(2).replace('.', ',')}
                                    </span>
                                    <h3 className="font-urbanist font-semibold text-white group-hover:text-amber-500 transition-colors">
                                        {product.name}
                                    </h3>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <p className="text-center text-zinc-500 font-urbanist text-sm mt-8">
                        Available at our shop
                    </p>
                </div>
            </section>

            {/* Instagram Feed */}
            <section className="py-24 px-6 md:px-12 bg-mural" data-testid="instagram-section">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-accent-pink font-urbanist font-semibold text-sm mb-2 block">FOLLOW US</span>
                        <h2 className="font-anton text-4xl md:text-5xl">@RLBARBER.SHOP</h2>
                        <p className="text-zinc-400 font-urbanist mt-4">Check out our latest work on Instagram</p>
                    </div>

                    {/* Instagram Embed */}
                    <div className="flex justify-center mb-8">
                        <a
                            href="https://www.instagram.com/rlbarber.shop/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:opacity-90 transition-opacity"
                            data-testid="instagram-follow-btn"
                        >
                            <Instagram size={24} className="text-white" />
                            <span className="font-anton text-white text-lg">FOLLOW ON INSTAGRAM</span>
                            <ExternalLink size={18} className="text-white opacity-70 group-hover:opacity-100" />
                        </a>
                    </div>

                    {/* Instagram Grid Preview - Your actual work */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {instagramImages.map((img, i) => (
                            <motion.a
                                key={img || `insta-${i}`}
                                href="https://www.instagram.com/rlbarber.shop/"
                                target="_blank"
                                rel="noopener noreferrer"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="relative aspect-square overflow-hidden group"
                                data-testid={`instagram-post-${i}`}
                            >
                                <img
                                    src={img}
                                    alt={`Instagram post ${i + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Instagram size={32} className="text-white" />
                                </div>
                            </motion.a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Reviews Section */}
            <section className="py-24 px-6 md:px-12 bg-mural-light" data-testid="reviews-section">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-accent-cyan font-urbanist font-semibold text-sm mb-2 block">REVIEWS</span>
                        <h2 className="font-anton text-4xl md:text-5xl">CLIENT LOVE</h2>
                        <p className="text-zinc-400 font-urbanist mt-4">What customers say about RL Barbershop</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {REVIEWS.map((review, i) => (
                            <motion.div
                                key={review.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="border border-zinc-800 bg-dark p-6"
                                data-testid={`review-card-${i}`}
                            >
                                <div className="flex items-center gap-1 mb-4">
                                    {Array.from({ length: review.rating }).map((_, idx) => (
                                        <Star key={idx} size={16} className="text-primary" />
                                    ))}
                                </div>
                                <p className="text-zinc-300 font-urbanist mb-6">“{review.text}”</p>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-white font-urbanist font-semibold">{review.name}</p>
                                        <p className="text-zinc-500 text-sm">{review.service}</p>
                                    </div>
                                    <span className="text-primary text-xs font-urbanist uppercase">Verified</span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Loyalty Program */}
            <section className="py-24 px-6 md:px-12 bg-mural" data-testid="loyalty-section">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 mb-12">
                        <div>
                            <span className="text-accent-pink font-urbanist font-semibold text-sm mb-2 block">LOYALTY PROGRAM</span>
                            <h2 className="font-anton text-4xl md:text-5xl">RL LOYALTY CLUB</h2>
                            <p className="text-zinc-400 font-urbanist mt-4 max-w-2xl">
                                VIP perks, priority slots, and seasonal rewards for loyal clients.
                            </p>
                        </div>
                        <Link to="/book" data-testid="loyalty-cta-btn">
                            <Button className="bg-primary text-black font-anton tracking-wider px-8 py-6 hover:bg-white">
                                JOIN THE CLUB
                            </Button>
                        </Link>
                    </div>

                    <div className="max-w-3xl">
                        <div className="border border-zinc-800 bg-dark-paper p-6" data-testid="loyalty-vip-card">
                            <div className="w-12 h-12 border border-primary flex items-center justify-center mb-4">
                                <Star size={22} className="text-primary" />
                            </div>
                            <h3 className="font-anton text-2xl mb-3">{vipPerks.title || DEFAULT_VIP_PERKS.title}</h3>
                            <p className="text-zinc-400 font-urbanist">
                                {vipPerks.description || DEFAULT_VIP_PERKS.description}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Locations with Google Maps */}
            <section className="py-24 px-6 md:px-12 bg-mural-light" data-testid="locations-section">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-accent-yellow font-urbanist font-semibold text-sm mb-2 block">FIND US</span>
                        <h2 className="font-anton text-4xl md:text-5xl">OUR LOCATION</h2>
                    </div>

                    <div className="max-w-2xl mx-auto">
                        {Object.entries(LOCATIONS).map(([key, location], i) => (
                            <motion.div
                                key={key}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.2 }}
                                className="border border-zinc-800 overflow-hidden"
                                data-testid={`location-card-${key}`}
                            >
                                {/* Map */}
                                <div className="h-64 bg-zinc-900">
                                    <iframe
                                        src={`https://www.google.com/maps?q=${encodeURIComponent(location.address)}&output=embed`}
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        allowFullScreen=""
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        title={`Map of ${location.name}`}
                                    />
                                </div>
                                
                                {/* Location Info */}
                                <div className="p-6 bg-dark">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="font-anton text-2xl text-white mb-2">{location.name}</h3>
                                            <p className="text-zinc-400 font-urbanist flex items-center gap-2">
                                                <MapPin size={16} className="text-primary" />
                                                {location.address}
                                            </p>
                                        </div>
                                        <a
                                            href={location.directionsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-2 border border-primary text-primary hover:bg-primary hover:text-black transition-colors font-urbanist font-semibold text-sm"
                                            data-testid={`directions-btn-${key}`}
                                        >
                                            Get Directions
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-6 md:px-12 bg-primary" data-testid="cta-section">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-anton text-4xl md:text-6xl text-black mb-6">
                        READY FOR A<br />FRESH CUT?
                    </h2>
                    <p className="font-urbanist text-xl text-black/70 mb-8">
                        Book your appointment now and experience the RL difference.
                    </p>
                    <Link to="/book" data-testid="cta-book-btn">
                        <Button className="bg-black text-white font-anton text-lg tracking-wider px-12 py-6 hover:bg-zinc-900 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none">
                            BOOK NOW
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 md:px-12 border-t border-zinc-800" data-testid="footer">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-12">
                        <div>
                            <img
                                src="https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/03303zlc_96f21524-c6ba-4411-86fb-3aa42208d697.jpeg"
                                alt="RL Barbershop"
                                className="h-16 w-16 mb-4"
                            />
                            <p className="text-zinc-500 font-urbanist text-sm">
                                Premium barbershop experience in Malta since 2020.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-anton text-lg mb-4">QUICK LINKS</h4>
                            <div className="space-y-2">
                                <Link to="/" className="block text-zinc-500 hover:text-primary font-urbanist" data-testid="footer-home-link">Home</Link>
                                <Link to="/services" className="block text-zinc-500 hover:text-primary font-urbanist" data-testid="footer-services-link">Services</Link>
                                <Link to="/book" className="block text-zinc-500 hover:text-primary font-urbanist" data-testid="footer-book-link">Book Now</Link>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-anton text-lg mb-4">HOURS</h4>
                            <p className="text-zinc-500 font-urbanist text-sm">Monday - Saturday</p>
                            <p className="text-white font-urbanist">9:00 AM - 7:00 PM</p>
                            <p className="text-zinc-500 font-urbanist text-sm mt-2">Sunday</p>
                            <p className="text-white font-urbanist">Closed</p>
                        </div>
                        <div>
                            <h4 className="font-anton text-lg mb-4">FOLLOW US</h4>
                            <div className="flex gap-4">
                                <a
                                    href="https://www.instagram.com/rlbarber.shop/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-zinc-500 hover:text-accent-pink"
                                    data-testid="instagram-link"
                                >
                                    <Instagram size={24} />
                                </a>
                                <a
                                    href="https://www.facebook.com/RiccardoLaudaniBarbershop"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-zinc-500 hover:text-accent-cyan"
                                    data-testid="facebook-link"
                                >
                                    <Facebook size={24} />
                                </a>
                            </div>
                            <p className="text-zinc-500 font-urbanist text-sm mt-4">
                                <Phone size={16} className="inline mr-2" />
                                +356 9933 6503
                            </p>
                        </div>
                    </div>
                    <div className="border-t border-zinc-800 pt-8 text-center">
                        <p className="text-zinc-600 font-urbanist text-sm">
                            © 2025 RL Barbershop. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>

            {shareOpen && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] px-6"
                    data-testid="share-qr-modal"
                >
                    <div className="bg-dark-paper border border-zinc-800 p-6 max-w-sm w-full relative">
                        <button
                            onClick={() => setShareOpen(false)}
                            className="absolute top-4 right-4 text-zinc-500 hover:text-white"
                            data-testid="share-qr-close"
                        >
                            <X size={18} />
                        </button>
                        <p className="text-zinc-500 text-xs uppercase font-urbanist mb-2">Share Link</p>
                        <h3 className="font-anton text-2xl mb-4">Scan to open</h3>
                        <div className="bg-white p-4 inline-block" data-testid="share-qr-code" ref={qrRef}>
                            <QRCodeCanvas value={resolvedShareUrl || window.location.origin} size={180} />
                        </div>
                        <p className="text-zinc-400 text-sm mt-4" data-testid="share-qr-url">
                            {resolvedShareUrl || 'Loading...'}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-4">
                            <Button
                                onClick={handleCopyLink}
                                data-testid="share-qr-copy-button"
                                className="bg-primary text-black font-anton tracking-wider px-6 py-3"
                            >
                                Copy Link
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleDownloadQR}
                                data-testid="share-qr-download-button"
                                className="border-zinc-700 text-zinc-300 font-anton tracking-wider px-6 py-3"
                            >
                                <Download size={16} className="mr-2" />
                                Download QR
                            </Button>
                            {whatsappShareUrl && (
                                <a
                                    href={whatsappShareUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="share-qr-whatsapp"
                                    className="inline-flex items-center justify-center border border-[#25D366] text-[#25D366] font-anton tracking-wider px-6 py-3 hover:bg-[#25D366] hover:text-black"
                                >
                                    <MessageCircle size={16} className="mr-2" />
                                    WhatsApp
                                </a>
                            )}
                            <span className="text-zinc-500 text-sm" data-testid="share-qr-copy-status">
                                {copyStatus}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LandingPage;
